import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Heart, ZoomIn, ZoomOut, Info, Palette as PaletteIcon, Camera, Image as ImageIcon, Box, CheckCircle, AlertCircle } from 'lucide-react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { useAuthStore } from '../../stores/authStore';
import { useGalleryStore } from '../../stores/galleryStore';
import { useUnity } from '../../contexts/UnityContext';
import analyticsService from '../../services/analyticsService';
import favoritesService from '../../services/favoritesService';
import { useStorageUrl } from '../../hooks/useStorageUrl';
import FrameSelector from './FrameSelector';
import FramePreview from './FramePreview';
import { frameService } from '../../services/frameService';
import {
  clampFrameOffset,
  resolveFrameMatOption,
} from '../../lib/frameCustomization';
import type { Piece, Frame, MatOption } from '@shared/types/firestore';
import type { ViewerEnvironment } from '../../types/unity';

interface PieceViewerProps {
  piece: Piece;
  viewMode?: '2d' | '3d';
  onViewModeChange?: (mode: '2d' | '3d') => void;
}

type PieceWithGalleryVariants = Piece & {
  gallery_thumbnail_path?: string | null;
  gallery_preview_path?: string | null;
};

function getDetailedDisplayImagePath(piece: Piece): string | null | undefined {
  const pieceWithVariants = piece as PieceWithGalleryVariants;

  return (
    piece.image_full_path ||
    pieceWithVariants.gallery_preview_path
  );
}

export default function PieceViewer({ piece, viewMode: propViewMode, onViewModeChange }: PieceViewerProps) {
  const { user, galleryId, sessionId } = useAuthStore();
  const userId = user?.userId ?? user?.id;
  const { collection, artists, loadFavorites } = useGalleryStore();
  const unity = useUnity();
  const {
    loadPiece,
    isUnityReady,
    nextCamera,
    applyFrame,
    clearFrame,
    requestSnapshot,
    isSnapshotting,
    snapshotStatus,
    snapshotUrl,
    snapshotError,
    resetSnapshot,
    setEnvironment,
    environments,
    currentEnvironment,
  } = unity;
  const [isFavorite, setIsFavorite] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showFrameSelector, setShowFrameSelector] = useState(false);
  const [frameSelectorExpandedFamily, setFrameSelectorExpandedFamily] = useState<string | null>(null);
  const [frameSelectorScrollAnchor, setFrameSelectorScrollAnchor] = useState<string | null>(null);
  const [localSnapshotStatus, setLocalSnapshotStatus] = useState<'idle' | 'pending' | 'complete' | 'error'>('idle');
  const [selectedFrame, setSelectedFrame] = useState<Frame | null>(null);
  const [selectedMatColor, setSelectedMatColor] = useState<MatOption | null>(null);
  const [selectedFrameOffset, setSelectedFrameOffset] = useState(0);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
  const [nextImageUrl, setNextImageUrl] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentPieceId, setCurrentPieceId] = useState<string | null>(null);
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);
  const lastPayloadRef = useRef<{ url: string; width?: number; height?: number } | null>(null);
  const lastSentPayloadRef = useRef<string | null>(null);
  const lastSentFramePayloadRef = useRef<string | null>(null);
  const frameSelectionSaveQueueRef = useRef<Promise<void>>(Promise.resolve());

  // Use prop viewMode or default to 3d
  const viewMode = propViewMode || '3d';
  const hideTitles = user?.hideTitles === true;
  const artworkLabel = hideTitles ? 'Artwork' : piece.title;
  const snapshotState = snapshotStatus || localSnapshotStatus;
  const snapshotBusy = isSnapshotting === true || snapshotState === 'pending';
  const snapshotComplete = snapshotState === 'complete' || Boolean(snapshotUrl);
  const snapshotFailed = snapshotState === 'error' || Boolean(snapshotError);
  const normalizedEnvironments = environments.map(option => ({ id: option, label: option }));
  const selectedEnvironment = currentEnvironment || normalizedEnvironments[0]?.id || '';
  const [isFavoriteUpdating, setIsFavoriteUpdating] = useState(false);
  const detailedDisplayImagePath = getDetailedDisplayImagePath(piece);

  useEffect(() => {
    setLocalSnapshotStatus('idle');
    resetSnapshot();
  }, [piece.id, resetSnapshot]);
  
  // Get the image URLs using the storage hook.
  // Unity gets the full-size asset; 2D display can temporarily fall back to the gallery preview.
  const { url: displayImageUrl, loading: imageLoading } = useStorageUrl(
    detailedDisplayImagePath
  );
  const { url: unityImageUrl } = useStorageUrl(
    piece.image_full_path
  );

  // Handle piece changes and image preloading
  // Load existing frame selection for the current piece
  const loadExistingFrameSelection = useCallback(async () => {
    if (!userId || !galleryId || !piece.id) {
      setSelectedFrame(null);
      setSelectedMatColor(null);
      setSelectedFrameOffset(0);
      return;
    }

    try {
      const frameSelection = await frameService.getPieceFrameSelection(galleryId, userId, piece.id);
      
      if (frameSelection) {
        // Load the full frame data
        const frame = await frameService.findFrameById(galleryId, frameSelection.frameId);
        if (frame) {
          setSelectedFrame(frame);
          
          // Find the selected mat color (match by hex color stored in selection)
          setSelectedMatColor(resolveFrameMatOption(frame, frameSelection.matColor));
          setSelectedFrameOffset(clampFrameOffset(frameSelection.offsetX));
        } else {
          setSelectedFrame(null);
          setSelectedMatColor(null);
          setSelectedFrameOffset(0);
        }
      } else {
        setSelectedFrame(null);
        setSelectedMatColor(null);
        setSelectedFrameOffset(0);
      }
    } catch (error) {
      console.error('Failed to load existing frame selection:', error);
      setSelectedFrame(null);
      setSelectedMatColor(null);
      setSelectedFrameOffset(0);
    }
  }, [galleryId, piece.id, userId]);

  useEffect(() => {
    // If this is a new piece, start the transition process
    if (piece.id !== currentPieceId) {
      setCurrentPieceId(piece.id ?? null);
      setIsTransitioning(true);
      setNextImageUrl(null);
      setSelectedFrame(null);
      setSelectedMatColor(null);
      setFrameSelectorExpandedFamily(null);
      setFrameSelectorScrollAnchor(null);

      // Load existing frame selection for this piece
      loadExistingFrameSelection();
    }
  }, [currentPieceId, loadExistingFrameSelection, piece.id]);

  // Handle when new image URL is ready
  useEffect(() => {
    if (displayImageUrl && piece.id === currentPieceId) {
      setNextImageUrl(displayImageUrl);
      
      // Wait a moment then transition
      const timer = setTimeout(() => {
        setCurrentImageUrl(displayImageUrl);
        setNextImageUrl(null);
        setIsTransitioning(false);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [displayImageUrl, imageLoading, piece.id, currentPieceId, currentImageUrl]);

  // Send Unity message with Firebase download URL and piece dimensions (inches)
  useEffect(() => {
    if (!isUnityReady) return;
    if (!unityImageUrl) return;
    if (piece.id !== currentPieceId) return;

    const widthNum = piece.width_inches ? parseFloat(piece.width_inches) : undefined;
    const heightNum = piece.height_inches ? parseFloat(piece.height_inches) : undefined;

    const payload = {
      url: unityImageUrl,
      width: widthNum,
      height: heightNum,
    };
    lastPayloadRef.current = payload;
    const paramStr = JSON.stringify(payload);
    if (lastSentPayloadRef.current === paramStr) {
      // Skip duplicate payload while Unity is initializing or unchanged
      return;
    }
    lastSentPayloadRef.current = paramStr;
    loadPiece(payload);
  }, [isUnityReady, unityImageUrl, piece.id, currentPieceId, loadPiece, piece.height_inches, piece.width_inches]);

  // Ensure Unity shows the correct piece when switching from 2D to 3D
  useEffect(() => {
    if (viewMode !== '3d') return;
    if (!isUnityReady) return;
    const payload = lastPayloadRef.current;
    if (payload) {
      try {
        loadPiece(payload);
      } catch (e) {
        console.warn('Failed to resend payload to Unity on 3D switch', e);
      }
    }
  }, [viewMode, isUnityReady, loadPiece]);

  useEffect(() => {
    if (viewMode !== '3d') return;
    if (!isUnityReady) return;

    if (!selectedFrame) {
      if (lastSentFramePayloadRef.current === null) {
        return;
      }

      lastSentFramePayloadRef.current = null;
      clearFrame();
      return;
    }

    const payload = {
      addressableKey: selectedFrame.addressableKey,
      matColorHex: selectedMatColor?.hexColor || '#FFFFFF',
      offsetX: selectedFrameOffset,
      offsetY: selectedFrameOffset,
    };
    const serializedPayload = JSON.stringify(payload);

    if (lastSentFramePayloadRef.current === serializedPayload) {
      return;
    }

    lastSentFramePayloadRef.current = serializedPayload;
    applyFrame(payload);
  }, [viewMode, isUnityReady, applyFrame, clearFrame, selectedFrame, selectedFrameOffset, selectedMatColor]);

  // Find the artist for this piece
  const artist = artists.find(a => a.artistId === piece.artistID);

  useEffect(() => {
    // Record piece view when component mounts
    if (user && galleryId && collection && sessionId) {
      void analyticsService.recordPieceView(
        user,
        galleryId,
        collection.collectionId,
        piece.id!,
        sessionId
      );
    }

    // Load initial favorite status and subscribe to changes
    if (user?.userId && piece.id) {
      const unsubscribe = favoritesService.subscribeToFavoriteStatus(
        user.userId,
        piece.id,
        (isFavorited) => {
          setIsFavorite(isFavorited);
          if (galleryId) {
            void loadFavorites(user.userId!, galleryId);
          }
        },
        galleryId
      );

      // Load initial state
      favoritesService.isFavorited(user.userId, piece.id, galleryId).then(setIsFavorite);

      // Track activity for analytics
      const trackActivity = () => analyticsService.recordActivity();
      window.addEventListener('mousemove', trackActivity);
      window.addEventListener('keydown', trackActivity);
      window.addEventListener('scroll', trackActivity);

      return () => {
        void analyticsService.finalizeCurrentPieceView();
        unsubscribe();
        window.removeEventListener('mousemove', trackActivity);
        window.removeEventListener('keydown', trackActivity);
        window.removeEventListener('scroll', trackActivity);
      };
    }
  }, [piece.id, user, galleryId, collection, sessionId, loadFavorites]);

  const handleFavoriteToggle = async () => {
    if (isFavoriteUpdating || !user || !galleryId || !collection || !sessionId || !piece.id) return;

    const newFavoriteStatus = !isFavorite;
    setIsFavoriteUpdating(true);
    setIsFavorite(newFavoriteStatus);

    try {
      const confirmedFavoriteStatus = await analyticsService.toggleFavorite(
        user,
        galleryId,
        collection.collectionId,
        piece.id,
        sessionId,
        newFavoriteStatus
      );

      if (confirmedFavoriteStatus === null) {
        throw new Error('Favorite toggle failed');
      }

      setIsFavorite(confirmedFavoriteStatus);
      
      // Update the favorites cache
      favoritesService.updateCache(user.userId!, piece.id, confirmedFavoriteStatus, galleryId);
      await loadFavorites(user.userId!, galleryId);
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      // Revert on error
      setIsFavorite(!newFavoriteStatus);
    } finally {
      setIsFavoriteUpdating(false);
    }
  };

  const handleZoomInteraction = async (type: 'zoom_in' | 'zoom_out') => {
    if (!user || !galleryId || !collection || !sessionId) return;

    await analyticsService.recordInteractionEvent(
      user,
      galleryId,
      collection.collectionId,
      piece.id!,
      sessionId,
      type
    );
  };

  const handleDescriptionView = async () => {
    if (!user || !galleryId || !collection || !sessionId) return;

    if (!showInfo) {
      await analyticsService.recordInteractionEvent(
        user,
        galleryId,
        collection.collectionId,
        piece.id!,
        sessionId,
        'read_description'
      );
    }
    setShowInfo(!showInfo);
  };

  const handleFrameSelectorToggle = async () => {
    if (!user || !galleryId || !collection || !sessionId) return;

    if (!showFrameSelector) {
      await analyticsService.recordInteractionEvent(
        user,
        galleryId,
        collection.collectionId,
        piece.id!,
        sessionId,
        'frame_selector_open'
      );
    }
    setShowFrameSelector(!showFrameSelector);
  };

  const handleFrameSelect = async (
    frame: Frame | null,
    matColor?: MatOption,
    frameOffset = 0
  ) => {
    const previousFrameId = selectedFrame?.id;
    const resolvedMatColor = frame
      ? resolveFrameMatOption(frame, matColor?.hexColor)
      : null;
    const resolvedFrameOffset = frame ? clampFrameOffset(frameOffset) : 0;

    setSelectedFrame(frame);
    setSelectedMatColor(resolvedMatColor);
    setSelectedFrameOffset(resolvedFrameOffset);

    // Save frame selection to user preferences
    if (user && userId && galleryId && piece.id) {
      const saveSelection = async () => {
        if (frame) {
          await frameService.applyFrameToPiece(galleryId, userId, piece.id!, {
            frameId: frame.id!,
            matColor: resolvedMatColor?.hexColor || '#FFFFFF',
            offsetX: resolvedFrameOffset,
            offsetY: resolvedFrameOffset
          });
        } else {
          await frameService.removeFrameFromPiece(galleryId, userId, piece.id!);
        }
      };
      const saveOperation = frameSelectionSaveQueueRef.current
        .catch(() => undefined)
        .then(saveSelection);
      frameSelectionSaveQueueRef.current = saveOperation;

      try {
        await saveOperation;
      } catch (error) {
        console.error('Failed to save frame selection:', error);
      }
    }
    
    // Track analytics for frame selection
    if (user && galleryId && collection && sessionId && previousFrameId !== frame?.id) {
      await analyticsService.recordInteractionEvent(
        user,
        galleryId,
        collection.collectionId,
        piece.id!,
        sessionId,
        frame ? 'frame_applied' : 'frame_removed'
      );
    }
  };

  const handleSnapshot = async () => {
    if (!requestSnapshot || snapshotBusy) return;

    setLocalSnapshotStatus('pending');

    try {
      const accepted = requestSnapshot({ includeFrame: true });
      if (!accepted) {
        setLocalSnapshotStatus('error');
      }
    } catch (error) {
      console.warn('Failed to request Unity snapshot:', error);
      setLocalSnapshotStatus('error');
    }
  };

  const handleEnvironmentChange = (environmentId: ViewerEnvironment) => {
    if (!setEnvironment) return;

    try {
      setEnvironment(environmentId);
    } catch (error) {
      console.warn('Failed to set Unity environment:', error);
    }
  };

  return (
    <div className="flex-1 flex flex-col relative overflow-hidden">
      {/* Main Viewer Content */}
      {viewMode === '2d' ? (
        /* 2D Image Viewer */
        <div className="flex-1 relative overflow-hidden">
          <TransformWrapper
            initialScale={1}
            minScale={0.1}
            maxScale={3}
            wheel={{ step: 0.1 }}
            limitToBounds={true}
            centerOnInit={true}
            onZoom={(ref) => {
              if (ref.state.scale > ref.state.previousScale) {
                handleZoomInteraction('zoom_in');
              } else {
                handleZoomInteraction('zoom_out');
              }
            }}
          >
            {({ zoomIn, zoomOut, resetTransform }) => (
              <>
                <TransformComponent
                  wrapperStyle={{
                    width: '100%',
                    height: '100%',
                    cursor: 'grab'
                  }}
                  contentStyle={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                  }}
                >
                  <div className="relative w-full h-full flex items-center justify-center">
                    {currentImageUrl ? (
                      <div className="relative">
                        <motion.img
                          key={currentImageUrl}
                          initial={{ opacity: 1, scale: 1 }}
                          animate={{
                            opacity: isTransitioning ? 0.3 : 1,
                            scale: 1
                          }}
                          transition={{
                            duration: 0.3,
                            ease: "easeInOut"
                          }}
                          ref={setImageElement}
                          src={currentImageUrl}
                          alt={artworkLabel}
                          className="max-w-full max-h-full object-contain"
                          draggable={false}
                          style={{
                            maxWidth: 'calc(100vw - 320px - 40px)',
                            maxHeight: 'calc(100vh - 80px - 40px)',
                            width: 'auto',
                            height: 'auto'
                          }}
                        />

                        {/* Frame Preview Overlay */}
                        {selectedFrame && imageElement && (
                          <FramePreview
                            frame={selectedFrame}
                            matColor={selectedMatColor}
                            frameOffset={selectedFrameOffset}
                            imageWidth={imageElement.naturalWidth}
                            imageHeight={imageElement.naturalHeight}
                            className="absolute top-0 left-0"
                          />
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center w-96 h-96 bg-gray-100 rounded">
                        <span className="text-gray-500">No image</span>
                      </div>
                    )}

                    {/* Show loading overlay when transitioning */}
                    {isTransitioning && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 flex items-center justify-center bg-black/40"
                      >
                        <div className="flex flex-col items-center space-y-3">
                          <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
                          <span className="text-white text-sm font-medium">Loading next artwork...</span>
                        </div>
                      </motion.div>
                    )}

                    {/* Preload next image invisibly */}
                    {nextImageUrl && (
                      <img
                        src={nextImageUrl}
                        alt=""
                        className="absolute opacity-0 pointer-events-none"
                      />
                    )}
                  </div>
                </TransformComponent>

                {/* Zoom Controls */}
                <div className="absolute bottom-4 right-4 flex flex-col space-y-2">
                  <button
                    onClick={() => zoomIn()}
                    className="p-2 bg-black/50 text-white rounded-lg hover:bg-black/70 transition-colors backdrop-blur-sm"
                    title="Zoom In"
                  >
                    <ZoomIn className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => zoomOut()}
                    className="p-2 bg-black/50 text-white rounded-lg hover:bg-black/70 transition-colors backdrop-blur-sm"
                    title="Zoom Out"
                  >
                    <ZoomOut className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => resetTransform()}
                    className="p-2 bg-black/50 text-white rounded-lg hover:bg-black/70 transition-colors backdrop-blur-sm text-xs"
                    title="Reset Zoom"
                  >
                    1:1
                  </button>
                </div>
              </>
            )}
          </TransformWrapper>
        </div>
      ) : (
        <div className="flex-1 pointer-events-none" />
      )}

      {/* Controls Overlay */}
      <div className="absolute top-4 right-4 z-20 flex flex-wrap justify-end gap-2">
        {onViewModeChange && (
          <div className="flex items-center rounded-lg bg-black/50 p-1 text-white backdrop-blur-sm">
            <button
              onClick={() => onViewModeChange('2d')}
              className={`flex items-center space-x-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                viewMode === '2d' ? 'bg-white text-gray-900' : 'text-white hover:bg-white/10'
              }`}
              title="View in 2D"
              aria-pressed={viewMode === '2d'}
            >
              <ImageIcon className="w-4 h-4" />
              <span>2D</span>
            </button>
            <button
              onClick={() => onViewModeChange('3d')}
              className={`flex items-center space-x-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                viewMode === '3d' ? 'bg-white text-gray-900' : 'text-white hover:bg-white/10'
              }`}
              title="View in 3D"
              aria-pressed={viewMode === '3d'}
            >
              <Box className="w-4 h-4" />
              <span>3D</span>
            </button>
          </div>
        )}

        {viewMode === '3d' && setEnvironment && normalizedEnvironments.length > 0 && (
          <select
            value={selectedEnvironment}
            onChange={(event) => handleEnvironmentChange(event.target.value as ViewerEnvironment)}
            disabled={!isUnityReady}
            className="h-9 max-w-40 rounded-lg border border-white/20 bg-black/50 px-2 text-xs font-medium text-white backdrop-blur-sm hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-white/60"
            title="Select environment"
            aria-label="Select environment"
          >
            {normalizedEnvironments.map(option => (
              <option key={option.id} value={option.id} className="bg-gray-900 text-white">
                {option.label}
              </option>
            ))}
          </select>
        )}

        <button
          onClick={handleFavoriteToggle}
          disabled={isFavoriteUpdating}
          className={`p-2 rounded-lg transition-colors backdrop-blur-sm disabled:cursor-not-allowed disabled:opacity-60 ${
            isFavorite
              ? 'bg-red-500 text-white'
              : 'bg-black/50 text-white hover:bg-black/70'
          }`}
          title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        >
          <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
        </button>

        {/* Camera button (3D mode) */}
        {viewMode === '3d' && (
          <button
            onClick={nextCamera}
            className="p-2 bg-black/50 text-white rounded-lg hover:bg-black/70 transition-colors backdrop-blur-sm"
            title="Next Camera"
          >
            <Camera className="w-5 h-5" />
          </button>
        )}

        {viewMode === '3d' && requestSnapshot && (
          <button
            onClick={handleSnapshot}
            disabled={snapshotBusy}
            aria-label={
              snapshotComplete
                ? 'Snapshot complete'
                : snapshotFailed
                ? snapshotError || 'Snapshot failed'
                : snapshotBusy
                ? 'Capturing snapshot'
                : 'Capture snapshot'
            }
            className={`p-2 rounded-lg transition-colors backdrop-blur-sm ${
              snapshotComplete
                ? 'bg-green-500 text-white'
                : snapshotFailed
                ? 'bg-red-500 text-white'
                : 'bg-black/50 text-white hover:bg-black/70 disabled:cursor-not-allowed disabled:opacity-70'
            }`}
            title={
              snapshotComplete
                ? 'Snapshot complete'
                : snapshotFailed
                ? snapshotError || 'Snapshot failed'
                : snapshotBusy
                ? 'Capturing snapshot'
                : 'Capture snapshot'
            }
          >
            {snapshotComplete ? (
              <CheckCircle className="w-5 h-5" />
            ) : snapshotFailed ? (
              <AlertCircle className="w-5 h-5" />
            ) : snapshotBusy ? (
              <span className="block w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
            ) : (
              <Camera className="w-5 h-5" />
            )}
          </button>
        )}

        <span className="sr-only" aria-live="polite">
          {snapshotComplete
            ? 'Snapshot complete'
            : snapshotFailed
            ? snapshotError || 'Snapshot failed'
            : snapshotBusy
            ? 'Capturing snapshot'
            : ''}
        </span>

        {/* Frame selector (3D only) */}
        {viewMode === '3d' && (
          <button
            onClick={handleFrameSelectorToggle}
            className={`p-2 rounded-lg transition-colors backdrop-blur-sm flex items-center space-x-1 relative ${
              showFrameSelector
                ? 'bg-blue-500 text-white'
                : selectedFrame
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-black/50 text-white hover:bg-black/70'
            }`}
            title={
              showFrameSelector
                ? "Close frame selector"
                : selectedFrame
                ? `Frame applied: ${selectedFrame.name}`
                : "Add frame to artwork"
            }
          >
            <PaletteIcon className="w-5 h-5" />
            <span className="text-xs font-medium hidden sm:inline">
              {selectedFrame ? 'Framed' : 'Frame'}
            </span>
            {selectedFrame && !showFrameSelector && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div>
            )}
          </button>
        )}

        {/* Info toggle (2D view only) */}
        {viewMode === '2d' && (
          <button
            onClick={handleDescriptionView}
            className={`p-2 rounded-lg transition-colors backdrop-blur-sm ${
              showInfo
                ? 'bg-primary-500 text-white'
                : 'bg-black/50 text-white hover:bg-black/70'
            }`}
            title="Toggle information"
          >
            <Info className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Information Panel */}
      {showInfo && (
        <motion.div
          initial={{ opacity: 0, x: 300 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 300 }}
          transition={{ duration: 0.3 }}
          className="absolute top-0 right-0 w-80 h-full bg-white/95 backdrop-blur-sm border-l border-gray-200 p-6 overflow-y-auto"
        >
          <div className="space-y-4">
            <div>
              {!hideTitles && (
                <h2 className="text-xl font-bold text-gray-900">{piece.title}</h2>
              )}
              {artist && (
                <p className={`text-lg text-gray-700 ${hideTitles ? '' : 'mt-1'}`}>{artist.displayName}</p>
              )}
              {piece.date && (
                <p className="text-sm text-gray-500 mt-1">{piece.date}</p>
              )}
            </div>

            {piece.description && (
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Description</h3>
                <p className="text-sm text-gray-700 leading-relaxed">{piece.description}</p>
              </div>
            )}

            <div>
              <h3 className="font-medium text-gray-900 mb-2">Details</h3>
              <div className="space-y-1 text-sm text-gray-600">
                {piece.width_inches && piece.height_inches && (
                  <p>Dimensions: {piece.width_inches}" × {piece.height_inches}"</p>
                )}
                {piece.notes && (
                  <p className="mt-2">{piece.notes}</p>
                )}
              </div>
            </div>

            {artist && artist.bio && (
              <div>
                <h3 className="font-medium text-gray-900 mb-2">About the Artist</h3>
                <p className="text-sm text-gray-700 leading-relaxed">{artist.bio}</p>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Frame Selector Panel */}
      {showFrameSelector && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/20 backdrop-blur-sm"
            onClick={() => setShowFrameSelector(false)}
          />
          
          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, x: 320 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 320 }}
            transition={{ duration: 0.3 }}
            className="absolute top-0 right-0 h-full bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <FrameSelector
              onFrameSelect={handleFrameSelect}
              onClose={() => setShowFrameSelector(false)}
              selectedFrame={selectedFrame}
              selectedMatColor={selectedMatColor}
              selectedFrameOffset={selectedFrameOffset}
              expandedFamily={frameSelectorExpandedFamily}
              onExpandedFamilyChange={setFrameSelectorExpandedFamily}
              scrollAnchorId={frameSelectorScrollAnchor}
              onScrollAnchorChange={setFrameSelectorScrollAnchor}
            />
          </motion.div>
        </>
      )}
    </div>
  );
}
